import React from 'react';
import { Box, Card, CardProps, SxProps, Theme, Typography, styled, useTheme } from '@mui/material';

export type SurfaceCardProps = Omit<CardProps, 'title'> & {
    title?: React.ReactNode;
    action?: React.ReactNode;
    contentSx?: SxProps<Theme>;
    headerSx?: SxProps<Theme>;
};

export default function SurfaceCard({
    title,
    action,
    contentSx,
    headerSx,
    children,
    sx,
    variant,
    elevation,
    ...cardProps
}: SurfaceCardProps) {
    const theme = useTheme();

    return (
        <Card
            variant={variant ?? 'outlined'}
            elevation={elevation ?? 0}
            {...cardProps}
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.background.paper,
                borderRadius: theme.shape.borderRadius,
                ...sx,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    padding: {
                        xs: theme.spacing(2, 1),
                        md: theme.spacing(2),
                    },
                    ...contentSx,
                }}
            >
                {(title || action) && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 0.5,
                            ...headerSx,
                        }}
                    >
                        {typeof title === 'string' ? (
                            <Typography variant="h5" fontWeight={800} noWrap sx={{ flex: 1, minWidth: 0 }}>
                                {title}
                            </Typography>
                        ) : (
                            title
                        )}
                        {action}
                    </Box>
                )}
                {children}
            </Box>
        </Card>
    );
}

export const DashboardCard = styled(SurfaceCard)`
    min-height: ${({ theme }) => theme.spacing(37.5)};
    ${({ theme }) => theme.breakpoints.up('md')} {
        min-width: ${({ theme }) => theme.spacing(47.5)};
    }
`;
